import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import EnvironmentManagerPanel from './EnvironmentManagerPanel.jsx';

import './SpecsManagerDialog.scss';

const MOCK_BASE_URL = process.env.REACT_APP_MOCK_BASE_URL || 'http://localhost';
const IS_PRODUCTION = MOCK_BASE_URL.includes('cloudmocks');

const StatusBadge = ({ isRunning }) => {
  const statusClass = isRunning ? 'status-running' : 'status-stopped';
  const statusText = isRunning ? 'Running' : 'Stopped';
  return <span className={`status-badge ${statusClass}`}>{statusText}</span>;
};

StatusBadge.propTypes = {
  isRunning: PropTypes.bool,
};

const getMockPath = (spec) => {
  const effectivePath = spec.basePath || spec.name;
  if (IS_PRODUCTION) {
    // Production: use cloudmocks URL without /mock prefix
    return `${MOCK_BASE_URL}/${effectivePath}`;
  }
  // Development: use localhost with prism port
  if (spec.prismPort) {
    return `${MOCK_BASE_URL}:${spec.prismPort}`;
  }
  return null;
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <button
      type="button"
      className="btn btn-copy btn-sm"
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? '✓' : '📋'}
    </button>
  );
};

CopyButton.propTypes = {
  text: PropTypes.string.isRequired,
};

const ProxyModeSelector = ({ spec, onUpdate, isUpdating }) => {
  // Use local state for optimistic updates
  const [localProxyMode, setLocalProxyMode] = useState(spec.proxyMode || 'mock');
  const [localUpstreamEnv, setLocalUpstreamEnv] = useState(spec.upstreamEnvironment || '');

  // Sync local state with props when spec changes (e.g., after successful API call)
  useEffect(() => {
    setLocalProxyMode(spec.proxyMode || 'mock');
    setLocalUpstreamEnv(spec.upstreamEnvironment || '');
  }, [spec.proxyMode, spec.upstreamEnvironment]);

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    // If switching to a mode that needs upstream and none is set, default to 'stage'
    let newUpstream = localUpstreamEnv;
    if ((newMode === 'fallback' || newMode === 'proxy') && !localUpstreamEnv) {
      newUpstream = 'stage';
    }
    // If switching to mock mode, clear upstream
    if (newMode === 'mock') {
      newUpstream = '';
    }

    // Optimistically update local state
    setLocalProxyMode(newMode);
    setLocalUpstreamEnv(newUpstream);

    // Trigger the API call
    onUpdate(spec.id, newUpstream, newMode);
  };

  const handleUpstreamChange = (e) => {
    const newUpstream = e.target.value;

    // Optimistically update local state
    setLocalUpstreamEnv(newUpstream);

    // Trigger the API call
    onUpdate(spec.id, newUpstream, localProxyMode);
  };

  const needsUpstream = localProxyMode === 'fallback' || localProxyMode === 'proxy';

  return (
    <div className="proxy-mode-selector">
      <select
        className="mode-select"
        value={localProxyMode}
        onChange={handleModeChange}
        disabled={isUpdating}
        title="Select how requests are handled"
      >
        <option value="mock">Mock Only</option>
        <option value="fallback">Mock + Fallback</option>
        <option value="proxy">Proxy Only</option>
      </select>
      {needsUpstream && (
        <select
          className="upstream-select"
          value={localUpstreamEnv}
          onChange={handleUpstreamChange}
          disabled={isUpdating}
          title="Select upstream environment"
        >
          <option value="stage">Stage</option>
          <option value="prelive">Prelive</option>
        </select>
      )}
    </div>
  );
};

ProxyModeSelector.propTypes = {
  spec: PropTypes.shape({
    id: PropTypes.number.isRequired,
    upstreamEnvironment: PropTypes.string,
    proxyMode: PropTypes.string,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  isUpdating: PropTypes.bool,
};

const GlobalProxyConfig = ({ config, onUpdate, isUpdating }) => {
  const [localEnabled, setLocalEnabled] = useState(config?.globalProxyEnabled || false);
  const [localUpstream, setLocalUpstream] = useState(config?.defaultUpstreamEnvironment || '');

  // Sync local state with props
  useEffect(() => {
    setLocalEnabled(config?.globalProxyEnabled || false);
    setLocalUpstream(config?.defaultUpstreamEnvironment || '');
  }, [config?.globalProxyEnabled, config?.defaultUpstreamEnvironment]);

  const handleEnabledChange = (e) => {
    const newEnabled = e.target.checked;
    setLocalEnabled(newEnabled);
    // If enabling and no upstream selected, default to 'stage'
    const upstream = newEnabled && !localUpstream ? 'stage' : localUpstream;
    if (newEnabled && !localUpstream) {
      setLocalUpstream('stage');
    }
    onUpdate(newEnabled, upstream);
  };

  const handleUpstreamChange = (e) => {
    const newUpstream = e.target.value;
    setLocalUpstream(newUpstream);
    onUpdate(localEnabled, newUpstream);
  };

  return (
    <div className="global-proxy-config">
      <h4 className="global-proxy-title">Global Proxy Settings</h4>
      <p className="global-proxy-description">
        Configure how unmatched requests (not handled by any spec) are processed.
      </p>
      <div className="global-proxy-controls">
        <label className="global-proxy-toggle">
          <input
            type="checkbox"
            checked={localEnabled}
            onChange={handleEnabledChange}
            disabled={isUpdating}
          />
          <span>Enable global proxy for unmatched requests</span>
        </label>
        {localEnabled && (
          <div className="global-proxy-upstream">
            <label>
              Default upstream:
              <select
                value={localUpstream}
                onChange={handleUpstreamChange}
                disabled={isUpdating}
              >
                <option value="stage">Stage</option>
                <option value="prelive">Prelive</option>
              </select>
            </label>
          </div>
        )}
      </div>
      {!localEnabled && (
        <p className="global-proxy-note">
          When disabled, unmatched requests will return 404.
        </p>
      )}
    </div>
  );
};

GlobalProxyConfig.propTypes = {
  config: PropTypes.shape({
    globalProxyEnabled: PropTypes.bool,
    defaultUpstreamEnvironment: PropTypes.string,
  }),
  onUpdate: PropTypes.func.isRequired,
  isUpdating: PropTypes.bool,
};

const BasePathEditor = ({ spec, onSave, isUpdating }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(spec.basePath || '');

  useEffect(() => {
    setValue(spec.basePath || '');
  }, [spec.basePath]);

  const handleSave = () => {
    onSave(spec.id, value.trim());
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') {
      setValue(spec.basePath || '');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="base-path-editor">
        <input
          type="text"
          className="base-path-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          disabled={isUpdating}
          placeholder={spec.name}
          autoFocus
        />
      </div>
    );
  }

  return (
    <div className="base-path-display" onClick={() => setIsEditing(true)} title="Click to edit custom path">
      <span className={`base-path-text ${spec.basePath ? 'custom' : 'default'}`}>
        /{spec.basePath || spec.name}
      </span>
      <span className="base-path-edit-icon">&#9998;</span>
    </div>
  );
};

BasePathEditor.propTypes = {
  spec: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    basePath: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  isUpdating: PropTypes.bool,
};

const SpecRow = ({
  spec,
  onDelete,
  onLoadIntoEditor,
  onUpdateUpstream,
  onUpdateBasePath,
  isDeleting,
  isUpstreamUpdating,
  activeEnvironment,
  envSpecSettings,
  onUpdateEnvSpecSetting,
}) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const mockPath = getMockPath(spec);

  const handleDeleteClick = () => {
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    onDelete(spec.id);
    setShowConfirmDelete(false);
  };

  const handleCancelDelete = () => {
    setShowConfirmDelete(false);
  };

  return (
    <tr className="spec-row">
      <td className="spec-name">{spec.name}</td>
      <td className="spec-title">{spec.title || '-'}</td>
      <td className="spec-version">{spec.version || '-'}</td>
      <td className="spec-status">
        <StatusBadge isRunning={spec.isRunning} />
      </td>
      <td className="spec-base-path">
        {!activeEnvironment && onUpdateBasePath ? (
          <BasePathEditor
            spec={spec}
            onSave={onUpdateBasePath}
            isUpdating={isUpstreamUpdating}
          />
        ) : (
          <span className="base-path-text">/{spec.basePath || spec.name}</span>
        )}
      </td>
      <td className="spec-proxy-mode">
        {activeEnvironment ? (
          <EnvProxyModeSelector
            spec={spec}
            envId={activeEnvironment.id}
            envSpecSettings={envSpecSettings}
            onUpdate={onUpdateEnvSpecSetting}
            isUpdating={isUpstreamUpdating}
          />
        ) : (
          <ProxyModeSelector
            spec={spec}
            onUpdate={onUpdateUpstream}
            isUpdating={isUpstreamUpdating}
          />
        )}
      </td>
      <td className="spec-mock-path">
        {mockPath && spec.isRunning ? (
          <div className="mock-path-container">
            <code className="mock-path-code">{mockPath}</code>
            <CopyButton text={mockPath} />
          </div>
        ) : (
          <span className="mock-path-unavailable">
            {spec.isRunning ? '-' : 'Not running'}
          </span>
        )}
      </td>
      <td className="spec-actions">
        {showConfirmDelete ? (
          <div className="confirm-delete">
            <span className="confirm-text">Delete?</span>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              Yes
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              No
            </button>
          </div>
        ) : (
          <div className="action-buttons">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => onLoadIntoEditor(spec)}
              title="Load into editor"
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={handleDeleteClick}
              title="Delete specification"
            >
              Delete
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

SpecRow.propTypes = {
  spec: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    title: PropTypes.string,
    version: PropTypes.string,
    isRunning: PropTypes.bool,
    prismPort: PropTypes.number,
    mockUrl: PropTypes.string,
    upstreamEnvironment: PropTypes.string,
    proxyMode: PropTypes.string,
    basePath: PropTypes.string,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onLoadIntoEditor: PropTypes.func.isRequired,
  onUpdateUpstream: PropTypes.func,
  onUpdateBasePath: PropTypes.func,
  isDeleting: PropTypes.bool,
  isUpstreamUpdating: PropTypes.bool,
  activeEnvironment: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  envSpecSettings: PropTypes.array,
  onUpdateEnvSpecSetting: PropTypes.func,
};

const EnvironmentSelector = ({ environments, activeEnvironment, onSelect, isLoading }) => {
  const handleChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      onSelect(null);
    } else {
      const env = environments.find((env) => String(env.id) === value);
      if (env) {
        onSelect({ id: env.id, name: env.name });
      }
    }
  };

  return (
    <div className="environment-selector">
      <label className="env-selector-label">
        <span className="env-selector-text">Viewing config for:</span>
        <select
          className="env-selector-dropdown"
          value={activeEnvironment ? String(activeEnvironment.id) : ''}
          onChange={handleChange}
          disabled={isLoading}
        >
          <option value="">Default (no environment)</option>
          {environments.map((env) => (
            <option key={env.id} value={String(env.id)}>
              {env.name}
            </option>
          ))}
        </select>
      </label>
      {activeEnvironment && (
        <span className="env-selector-badge">
          Environment: {activeEnvironment.name}
        </span>
      )}
    </div>
  );
};

EnvironmentSelector.propTypes = {
  environments: PropTypes.array,
  activeEnvironment: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  onSelect: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

const EnvProxyModeSelector = ({ spec, envId, envSpecSettings, onUpdate, isUpdating }) => {
  // Find existing setting for this env+spec
  const existingSetting = envSpecSettings?.find(
    (s) => s.specId === spec.id
  );

  const [localProxyMode, setLocalProxyMode] = useState(existingSetting?.proxyMode || spec.proxyMode || 'mock');
  const [localUpstreamEnv, setLocalUpstreamEnv] = useState(existingSetting?.upstreamEnvironment || spec.upstreamEnvironment || '');

  useEffect(() => {
    if (existingSetting) {
      setLocalProxyMode(existingSetting.proxyMode || 'mock');
      setLocalUpstreamEnv(existingSetting.upstreamEnvironment || '');
    } else {
      setLocalProxyMode(spec.proxyMode || 'mock');
      setLocalUpstreamEnv(spec.upstreamEnvironment || '');
    }
  }, [existingSetting?.proxyMode, existingSetting?.upstreamEnvironment, spec.proxyMode, spec.upstreamEnvironment]);

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    let newUpstream = localUpstreamEnv;
    if ((newMode === 'fallback' || newMode === 'proxy') && !localUpstreamEnv) {
      newUpstream = 'stage';
    }
    if (newMode === 'mock') {
      newUpstream = '';
    }
    setLocalProxyMode(newMode);
    setLocalUpstreamEnv(newUpstream);
    onUpdate(envId, spec.id, newMode, newUpstream);
  };

  const handleUpstreamChange = (e) => {
    const newUpstream = e.target.value;
    setLocalUpstreamEnv(newUpstream);
    onUpdate(envId, spec.id, localProxyMode, newUpstream);
  };

  const needsUpstream = localProxyMode === 'fallback' || localProxyMode === 'proxy';
  const isOverridden = existingSetting != null;

  return (
    <div className="proxy-mode-selector">
      {isOverridden && <span className="env-override-indicator" title="Overridden by environment">*</span>}
      <select
        className="mode-select"
        value={localProxyMode}
        onChange={handleModeChange}
        disabled={isUpdating}
        title="Select how requests are handled for this environment"
      >
        <option value="mock">Mock Only</option>
        <option value="fallback">Mock + Fallback</option>
        <option value="proxy">Proxy Only</option>
      </select>
      {needsUpstream && (
        <select
          className="upstream-select"
          value={localUpstreamEnv}
          onChange={handleUpstreamChange}
          disabled={isUpdating}
          title="Select upstream environment"
        >
          <option value="stage">Stage</option>
          <option value="prelive">Prelive</option>
        </select>
      )}
    </div>
  );
};

EnvProxyModeSelector.propTypes = {
  spec: PropTypes.object.isRequired,
  envId: PropTypes.number.isRequired,
  envSpecSettings: PropTypes.array,
  onUpdate: PropTypes.func.isRequired,
  isUpdating: PropTypes.bool,
};

const SpecsManagerDialog = ({
  isOpen,
  onClose,
  getComponent,
  backendSpecsActions,
  backendSpecsSelectors,
}) => {
  const [activeTab, setActiveTab] = useState('specs');

  const Modal = getComponent('Modal', true);
  const ModalHeader = getComponent('ModalHeader', true);
  const ModalTitle = getComponent('ModalTitle', true);
  const ModalBody = getComponent('ModalBody', true);
  const ModalFooter = getComponent('ModalFooter', true);

  // Existing state
  const specsList = backendSpecsSelectors?.selectSpecsList?.() || [];
  const isLoading = backendSpecsSelectors?.selectSpecsListLoading?.() || false;
  const error = backendSpecsSelectors?.selectSpecsListError?.();
  const isDeleting = backendSpecsSelectors?.selectDeleteLoading?.() || false;
  const deleteError = backendSpecsSelectors?.selectDeleteError?.();
  const isUpstreamUpdating = backendSpecsSelectors?.selectUpstreamUpdating?.() || false;
  const upstreamUpdateError = backendSpecsSelectors?.selectUpstreamUpdateError?.();
  const globalConfig = backendSpecsSelectors?.selectGlobalConfig?.();
  const isGlobalConfigUpdating = backendSpecsSelectors?.selectGlobalConfigUpdating?.() || false;
  const globalConfigError = backendSpecsSelectors?.selectGlobalConfigUpdateError?.();

  // Environment state
  const environments = backendSpecsSelectors?.selectEnvironments?.() || [];
  const environmentsLoading = backendSpecsSelectors?.selectEnvironmentsLoading?.() || false;
  const environmentsError = backendSpecsSelectors?.selectEnvironmentsError?.();
  const activeEnvironment = backendSpecsSelectors?.selectActiveEnvironment?.() || null;
  const environmentDetail = backendSpecsSelectors?.selectEnvironmentDetail?.() || null;
  const environmentDetailLoading = backendSpecsSelectors?.selectEnvironmentDetailLoading?.() || false;
  const createEnvLoading = backendSpecsSelectors?.selectCreateEnvironmentLoading?.() || false;
  const createEnvError = backendSpecsSelectors?.selectCreateEnvironmentError?.();
  const deleteEnvLoading = backendSpecsSelectors?.selectDeleteEnvironmentLoading?.() || false;
  const deleteEnvError = backendSpecsSelectors?.selectDeleteEnvironmentError?.();
  const envSpecSettingUpdating = backendSpecsSelectors?.selectEnvSpecSettingUpdating?.() || false;
  const envSpecSettingError = backendSpecsSelectors?.selectEnvSpecSettingError?.();

  useEffect(() => {
    if (isOpen && backendSpecsActions?.fetchSpecsList) {
      backendSpecsActions.fetchSpecsList();
    }
    if (isOpen && backendSpecsActions?.fetchGlobalConfig) {
      backendSpecsActions.fetchGlobalConfig();
    }
    if (isOpen && backendSpecsActions?.fetchEnvironments) {
      backendSpecsActions.fetchEnvironments();
    }
  }, [isOpen, backendSpecsActions]);

  // Fetch environment detail when active environment changes
  useEffect(() => {
    if (activeEnvironment && activeEnvironment.id && backendSpecsActions?.fetchEnvironmentDetail) {
      backendSpecsActions.fetchEnvironmentDetail(activeEnvironment.id);
    }
  }, [activeEnvironment?.id, backendSpecsActions]);

  const handleRefresh = useCallback(() => {
    if (backendSpecsActions?.fetchSpecsList) {
      backendSpecsActions.fetchSpecsList();
    }
    if (backendSpecsActions?.fetchEnvironments) {
      backendSpecsActions.fetchEnvironments();
    }
  }, [backendSpecsActions]);

  const handleDelete = useCallback(
    async (specId) => {
      if (backendSpecsActions?.deleteSpec) {
        await backendSpecsActions.deleteSpec(specId);
      }
    },
    [backendSpecsActions]
  );

  const handleUpdateUpstream = useCallback(
    async (specId, upstreamEnvironment, proxyMode) => {
      if (backendSpecsActions?.updateUpstreamEnvironment) {
        await backendSpecsActions.updateUpstreamEnvironment(specId, upstreamEnvironment, proxyMode);
      }
    },
    [backendSpecsActions]
  );

  const handleUpdateBasePath = useCallback(
    async (specId, basePath) => {
      if (backendSpecsActions?.updateUpstreamEnvironment) {
        // Find the spec to preserve current upstream/proxyMode values
        const spec = specsList.find((s) => s.id === specId);
        if (spec) {
          await backendSpecsActions.updateUpstreamEnvironment(
            specId,
            spec.upstreamEnvironment,
            spec.proxyMode,
            basePath
          );
        }
      }
    },
    [backendSpecsActions, specsList]
  );

  const handleUpdateGlobalConfig = useCallback(
    async (globalProxyEnabled, defaultUpstreamEnvironment) => {
      if (backendSpecsActions?.updateGlobalConfig) {
        await backendSpecsActions.updateGlobalConfig(
          globalProxyEnabled,
          defaultUpstreamEnvironment
        );
      }
    },
    [backendSpecsActions]
  );

  const handleLoadIntoEditor = useCallback(
    async (spec) => {
      if (backendSpecsActions?.loadSpecIntoEditor) {
        await backendSpecsActions.loadSpecIntoEditor(spec.id);
        // Also explicitly set current spec to ensure state is updated
        if (backendSpecsActions?.setCurrentSpec) {
          backendSpecsActions.setCurrentSpec({
            id: spec.id,
            name: spec.name || spec.title,
          });
        }
        onClose();
      }
    },
    [backendSpecsActions, onClose]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Environment handlers
  const handleSetActiveEnvironment = useCallback((env) => {
    if (backendSpecsActions?.setActiveEnvironment) {
      backendSpecsActions.setActiveEnvironment(env);
    }
  }, [backendSpecsActions]);

  const handleCreateEnvironment = useCallback(async (name, description) => {
    if (backendSpecsActions?.createEnvironment) {
      await backendSpecsActions.createEnvironment(name, description);
    }
  }, [backendSpecsActions]);

  const handleDeleteEnvironment = useCallback(async (envId) => {
    if (backendSpecsActions?.deleteEnvironment) {
      await backendSpecsActions.deleteEnvironment(envId);
    }
  }, [backendSpecsActions]);

  const handleFetchEnvironmentDetail = useCallback(async (envId) => {
    if (backendSpecsActions?.fetchEnvironmentDetail) {
      await backendSpecsActions.fetchEnvironmentDetail(envId);
    }
  }, [backendSpecsActions]);

  const handleUpsertEnvSpecSetting = useCallback(async (envId, specId, proxyMode, upstreamEnvironment) => {
    if (backendSpecsActions?.upsertEnvSpecSetting) {
      await backendSpecsActions.upsertEnvSpecSetting(envId, specId, proxyMode, upstreamEnvironment);
    }
  }, [backendSpecsActions]);

  if (!isOpen || !Modal) return null;

  return (
    <Modal isOpen={isOpen} contentLabel="Specs Manager">
      {ModalHeader && ModalTitle && (
        <ModalHeader>
          <ModalTitle>Specifications Manager</ModalTitle>
        </ModalHeader>
      )}
      {ModalBody && (
        <ModalBody>
          {/* Tabs */}
          <div className="specs-manager-tabs">
            <button
              type="button"
              className={`specs-manager-tab ${activeTab === 'specs' ? 'active' : ''}`}
              onClick={() => setActiveTab('specs')}
            >
              Specs & Config
            </button>
            <button
              type="button"
              className={`specs-manager-tab ${activeTab === 'environments' ? 'active' : ''}`}
              onClick={() => setActiveTab('environments')}
            >
              Environments {environments.length > 0 && `(${environments.length})`}
            </button>
          </div>

          {/* Specs & Config Tab */}
          {activeTab === 'specs' && (
            <div className="specs-manager-container">
              {/* Environment Selector */}
              {environments.length > 0 && (
                <EnvironmentSelector
                  environments={environments}
                  activeEnvironment={activeEnvironment}
                  onSelect={handleSetActiveEnvironment}
                  isLoading={environmentsLoading}
                />
              )}

              {(error || deleteError || upstreamUpdateError || globalConfigError || envSpecSettingError) && (
                <div className="specs-error-banner">
                  {error && <div>Error loading specs: {error}</div>}
                  {deleteError && <div>Error deleting spec: {deleteError}</div>}
                  {upstreamUpdateError && <div>Error updating upstream: {upstreamUpdateError}</div>}
                  {globalConfigError && <div>Error updating global config: {globalConfigError}</div>}
                  {envSpecSettingError && <div>Error updating environment setting: {envSpecSettingError}</div>}
                </div>
              )}

              {isLoading ? (
                <div className="specs-loading">Loading specifications...</div>
              ) : specsList.length === 0 ? (
                <div className="specs-empty">
                  No specifications found. Create a new spec to get started.
                </div>
              ) : (
                <div className="specs-table-wrapper">
                  <table className="specs-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Title</th>
                        <th>Version</th>
                        <th>Status</th>
                        <th>Path</th>
                        <th>Proxy Mode</th>
                        <th>Mock Path</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {specsList.map((spec) => (
                        <SpecRow
                          key={spec.id}
                          spec={spec}
                          onDelete={handleDelete}
                          onLoadIntoEditor={handleLoadIntoEditor}
                          onUpdateUpstream={activeEnvironment ? null : handleUpdateUpstream}
                          onUpdateBasePath={activeEnvironment ? null : handleUpdateBasePath}
                          isDeleting={isDeleting}
                          isUpstreamUpdating={activeEnvironment ? envSpecSettingUpdating : isUpstreamUpdating}
                          activeEnvironment={activeEnvironment}
                          envSpecSettings={environmentDetail?.specSettings}
                          onUpdateEnvSpecSetting={handleUpsertEnvSpecSetting}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!activeEnvironment && (
                <GlobalProxyConfig
                  config={globalConfig}
                  onUpdate={handleUpdateGlobalConfig}
                  isUpdating={isGlobalConfigUpdating}
                />
              )}
            </div>
          )}

          {/* Environments Tab */}
          {activeTab === 'environments' && (
            <div className="specs-manager-container">
              <EnvironmentManagerPanel
                environments={environments}
                isLoading={environmentsLoading}
                error={environmentsError}
                createError={createEnvError}
                deleteError={deleteEnvError}
                isCreating={createEnvLoading}
                isDeleting={deleteEnvLoading}
                onCreateEnvironment={handleCreateEnvironment}
                onDeleteEnvironment={handleDeleteEnvironment}
                onSelectEnvironment={handleSetActiveEnvironment}
                environmentDetail={environmentDetail}
                environmentDetailLoading={environmentDetailLoading}
                onFetchDetail={handleFetchEnvironmentDetail}
              />
            </div>
          )}
        </ModalBody>
      )}
      {ModalFooter && (
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleClose}>
            Close
          </button>
        </ModalFooter>
      )}
    </Modal>
  );
};

SpecsManagerDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  getComponent: PropTypes.func.isRequired,
  backendSpecsActions: PropTypes.shape({
    fetchSpecsList: PropTypes.func,
    deleteSpec: PropTypes.func,
    loadSpecIntoEditor: PropTypes.func,
    updateUpstreamEnvironment: PropTypes.func,
    fetchGlobalConfig: PropTypes.func,
    updateGlobalConfig: PropTypes.func,
    fetchEnvironments: PropTypes.func,
    createEnvironment: PropTypes.func,
    deleteEnvironment: PropTypes.func,
    fetchEnvironmentDetail: PropTypes.func,
    setActiveEnvironment: PropTypes.func,
    upsertEnvSpecSetting: PropTypes.func,
  }),
  backendSpecsSelectors: PropTypes.shape({
    selectSpecsList: PropTypes.func,
    selectSpecsListLoading: PropTypes.func,
    selectSpecsListError: PropTypes.func,
    selectDeleteLoading: PropTypes.func,
    selectDeleteError: PropTypes.func,
    selectUpstreamUpdating: PropTypes.func,
    selectUpstreamUpdateError: PropTypes.func,
    selectGlobalConfig: PropTypes.func,
    selectGlobalConfigUpdating: PropTypes.func,
    selectGlobalConfigUpdateError: PropTypes.func,
    selectEnvironments: PropTypes.func,
    selectEnvironmentsLoading: PropTypes.func,
    selectEnvironmentsError: PropTypes.func,
    selectActiveEnvironment: PropTypes.func,
    selectEnvironmentDetail: PropTypes.func,
    selectEnvironmentDetailLoading: PropTypes.func,
    selectCreateEnvironmentLoading: PropTypes.func,
    selectCreateEnvironmentError: PropTypes.func,
    selectDeleteEnvironmentLoading: PropTypes.func,
    selectDeleteEnvironmentError: PropTypes.func,
    selectEnvSpecSettingUpdating: PropTypes.func,
    selectEnvSpecSettingError: PropTypes.func,
  }),
};

SpecsManagerDialog.defaultProps = {
  backendSpecsActions: null,
  backendSpecsSelectors: null,
};

export default SpecsManagerDialog;
